/**
 * Architettura a moduli di Kipo.
 *
 * Il NUCLEO possiede le entità condivise (tenant, utenti, ruoli, entitlement,
 * aziende e contatti, documenti, notifiche, audit, ricerca, AI, worker).
 * Ogni MODULO dichiara qui il proprio manifest e contribuisce solo a punti di
 * estensione fissi: schede sulle entità condivise, azioni contestuali, widget
 * dashboard, trigger/azioni per le automazioni, tool dell'assistente, tipi di
 * documento, notifiche, impostazioni. Un modulo non altera le tabelle del
 * nucleo e non importa un altro modulo: comunica con eventi e punti di estensione.
 * L'attivazione per tenant vive nella tabella tenant_modules.
 */
import type { Permission } from "./roles.js";

export const MODULE_KEYS = ["projects", "crm", "sales", "marketing", "ai", "contracts", "expenses"] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

/** Moduli previsti dopo il lancio (mostrati come "in arrivo"). */
export const FUTURE_MODULES = ["email", "support", "hr", "inventory"] as const;

/** Entità condivise del nucleo su cui i moduli possono agganciarsi. */
export type SharedEntity = "company" | "contact" | "user" | "document";

/** Nome icona: risolto dal frontend sul set di icone della shell. */
export type IconName =
  | "home" | "check-list" | "bell" | "kanban" | "users" | "file-text" | "megaphone"
  | "sparkles" | "settings" | "calendar" | "gantt" | "rocket" | "message" | "receipt"
  | "git" | "sprint" | "list" | "chart" | "filter" | "building" | "phone" | "wallet"
  | "book" | "mail" | "form" | "flow" | "template" | "segment" | "box" | "search" | "plus" | "report" | "clock" | "briefcase" | "bank" | "card";

export interface NavItem {
  key: string;
  label: string;
  /** percorso relativo al modulo, es. "/projects/all" */
  to: string;
  icon: IconName;
  permission?: Permission;
}

export interface SettingDef {
  key: string;
  label: string;
  type: "boolean" | "string" | "number" | "select";
  default: unknown;
  options?: { value: string; label: string }[];
}

export interface AiToolDef {
  name: string;
  description: string;
  permission: Permission;
  /** true = il tool modifica dati: l'assistente chiede conferma prima di eseguirlo */
  mutates: boolean;
}

export interface ModuleExtensions {
  /** schede aggiunte alla scheda di un'entità condivisa */
  entityTabs?: { entity: SharedEntity; key: string; label: string; icon: IconName }[];
  /** azioni contestuali sulle entità condivise o di altri moduli (via evento) */
  actions?: { entity: SharedEntity | string; key: string; label: string; icon: IconName; permission: Permission }[];
  dashboardWidgets?: { key: string; label: string; size: "s" | "m" | "l" }[];
  automationTriggers?: { key: string; label: string }[];
  automationActions?: { key: string; label: string }[];
  documentTypes?: { key: string; label: string; prefix: string }[];
}

export interface ModuleManifest {
  key: ModuleKey;
  name: string;
  /** colore del modulo (proposta grafica C): header e accenti */
  color: string;
  /** tinta chiara per selezioni e badge */
  tint: string;
  /** colore leggibile su fondo chiaro per testi accentati */
  ink: string;
  icon: IconName;
  description: string;
  dependsOn: ModuleKey[];
  permissions: Permission[];
  navigation: NavItem[];
  /** permesso minimo per vedere il modulo nella navigazione */
  entryPermission: Permission;
  settings: SettingDef[];
  /** eventi di dominio emessi (bus interno) */
  events: string[];
  /** tipi di notifica del modulo; email = invio email predefinito (l'utente può cambiarlo) */
  notifications: { type: string; label: string; email: boolean }[];
  aiTools: AiToolDef[];
  jobs: { key: string; description: string; cron?: string }[];
  extensions: ModuleExtensions;
}

export const MODULES: Record<ModuleKey, ModuleManifest> = {
  projects: {
    key: "projects",
    entryPermission: "projects:read",
    name: "Progetti",
    color: "#2456E6",
    tint: "#EEF2FD",
    ink: "#1B3FA8",
    icon: "kanban",
    description: "Progetti, task, sprint, release, Gantt, richieste del cliente, timesheet, saturazione e ore fatturabili.",
    dependsOn: [],
    permissions: ["projects:read", "projects:write", "projects:manage", "projects:billing"],
    navigation: [
      { key: "all", label: "Tutti i progetti", to: "/projects", icon: "kanban" },
      { key: "my-tasks", label: "I miei task", to: "/projects/my-tasks", icon: "check-list" },
      { key: "timesheet", label: "Timesheet", to: "/projects/timesheet", icon: "clock", permission: "projects:write" },
      { key: "capacity", label: "Saturazione", to: "/projects/capacity", icon: "chart", permission: "projects:manage" },
    ],
    settings: [
      { key: "defaultWeekMode", label: "Settimana predefinita", type: "select", default: "WORK_WEEK", options: [{ value: "WORK_WEEK", label: "Lun–ven" }, { value: "FULL_WEEK", label: "Settimana piena" }] },
      { key: "dailyReminder", label: "Promemoria giornaliero dei task", type: "boolean", default: true },
      { key: "timesheetApproval", label: "Le ore registrate vanno approvate da un responsabile", type: "boolean", default: false },
      { key: "saturationLow", label: "Sotto saturazione sotto il (%)", type: "number", default: 70 },
      { key: "saturationHigh", label: "Sovra saturazione oltre il (%)", type: "number", default: 100 },
    ],
    events: ["projects.project.created", "projects.task.created", "projects.task.updated", "projects.task.closed", "projects.release.released", "projects.request.approved"],
    notifications: [
      { type: "task_assigned", label: "Mi viene assegnato un task", email: true },
      { type: "task_updated", label: "Commenti e modifiche ai miei task", email: true },
      { type: "task_mentioned", label: "Qualcuno mi menziona in un commento", email: true },
      { type: "task_due", label: "Riepilogo mattutino dei task in scadenza", email: true },
      { type: "request_created", label: "Nuova richiesta di un cliente", email: true },
      { type: "request_approved", label: "Esito delle mie richieste", email: true },
      { type: "release_created", label: "Nuova release pianificata", email: false },
      { type: "timesheet_rejected", label: "Un responsabile respinge le mie ore", email: true },
    ],
    aiTools: [
      { name: "projects_list", description: "Elenca i progetti attivi con avanzamento e margine", permission: "projects:read", mutates: false },
      { name: "projects_tasks_due", description: "Task in scadenza o in ritardo, filtrabili per progetto e assegnatario", permission: "projects:read", mutates: false },
      { name: "projects_create_task", description: "Crea un task in un progetto", permission: "projects:write", mutates: true },
      { name: "projects_margin", description: "Ore vendute, usate e margine previsto di un progetto", permission: "projects:read", mutates: false },
      { name: "projects_saturation", description: "Saturazione del team (pianificato ed effettivo) nelle prossime settimane", permission: "projects:manage", mutates: false },
      { name: "projects_recent_done", description: "Task chiusi di recente nei progetti", permission: "projects:read", mutates: false },
      { name: "projects_requests", description: "Richieste inviate al team e il loro stato", permission: "projects:read", mutates: false },
      { name: "projects_create_request", description: "Invia una richiesta al team del progetto (solo clienti)", permission: "projects:write", mutates: true },
    ],
    jobs: [{ key: "projects.daily-reminder", description: "Email mattutina con i task del giorno", cron: "0 7 * * 1-5" }],
    extensions: {
      entityTabs: [{ entity: "company", key: "projects", label: "Progetti", icon: "kanban" }],
      actions: [{ entity: "deal", key: "create-project", label: "Crea progetto dal deal", icon: "kanban", permission: "projects:write" }],
      dashboardWidgets: [{ key: "projects.today", label: "Task di oggi", size: "m" }],
      automationTriggers: [{ key: "projects.task.closed", label: "Task chiuso" }],
      automationActions: [{ key: "projects.create_task", label: "Crea un task" }],
      documentTypes: [],
    },
  },
  crm: {
    key: "crm",
    entryPermission: "contacts:read",
    name: "CRM",
    color: "#F26B1D",
    tint: "#FDEEE4",
    ink: "#B4541E",
    icon: "users",
    description: "Aziende, contatti, pipeline di vendita, attività e note.",
    dependsOn: [],
    permissions: ["deals:read", "deals:read_all", "deals:write", "activities:read", "activities:write", "settings:pipelines"],
    navigation: [
      { key: "pipeline", label: "Pipeline", to: "/crm", icon: "kanban" },
      { key: "companies", label: "Aziende", to: "/crm/companies", icon: "building" },
      { key: "contacts", label: "Contatti", to: "/crm/contacts", icon: "users" },
      { key: "activities", label: "Attività", to: "/crm/activities", icon: "calendar" },
      { key: "reports", label: "Report", to: "/crm/reports", icon: "report" },
    ],
    settings: [{ key: "staleDealDays", label: "Giorni dopo cui un deal è fermo", type: "number", default: 30 }],
    events: ["crm.deal.created", "crm.deal.stage_changed", "crm.deal.won", "crm.deal.lost", "crm.activity.due"],
    notifications: [
      { type: "deal_assigned", label: "Mi viene assegnato un deal", email: true },
      { type: "activity_due", label: "Attività in scadenza", email: false },
      { type: "deal_stale", label: "Un mio deal è fermo da 30 giorni", email: false },
    ],
    aiTools: [
      { name: "crm_search", description: "Cerca aziende, contatti e deal", permission: "contacts:read", mutates: false },
      { name: "crm_pipeline_summary", description: "Valore della pipeline per fase e deal fermi", permission: "deals:read", mutates: false },
      { name: "crm_create_activity", description: "Pianifica un'attività (chiamata, riunione, follow-up)", permission: "activities:write", mutates: true },
      { name: "crm_add_note", description: "Aggiunge una nota a un'azienda, contatto o deal", permission: "activities:write", mutates: true },
    ],
    jobs: [{ key: "crm.stale-deals", description: "Segnala i deal fermi da troppi giorni", cron: "30 7 * * 1-5" }],
    extensions: {
      entityTabs: [
        { entity: "company", key: "deals", label: "Deal", icon: "kanban" },
        { entity: "contact", key: "activities", label: "Attività", icon: "calendar" },
      ],
      dashboardWidgets: [{ key: "crm.pipeline", label: "Pipeline", size: "m" }],
      automationTriggers: [{ key: "crm.deal.won", label: "Deal vinto" }, { key: "crm.deal.stage_changed", label: "Deal cambia fase" }],
      automationActions: [{ key: "crm.create_activity", label: "Crea attività" }],
    },
  },
  sales: {
    key: "sales",
    entryPermission: "quotes:read",
    name: "Vendite e finanza",
    color: "#0E9F6E",
    tint: "#E6F6EF",
    ink: "#0E6B4B",
    icon: "file-text",
    description: "Preventivi, fatture attive e passive, scadenzario, prima nota, margini, SDI.",
    dependsOn: ["crm"],
    permissions: ["quotes:read", "quotes:write", "invoices:read", "invoices:write", "invoices:send_sdi", "finance:read", "finance:write"],
    navigation: [
      { key: "overview", label: "Panoramica", to: "/sales", icon: "chart", permission: "finance:read" },
      { key: "quotes", label: "Preventivi", to: "/sales/quotes", icon: "file-text" },
      { key: "invoices", label: "Fatture attive", to: "/sales/invoices", icon: "receipt", permission: "invoices:read" },
      { key: "payables", label: "Fatture passive", to: "/sales/payables", icon: "receipt", permission: "finance:read" },
      { key: "schedule", label: "Scadenzario", to: "/sales/schedule", icon: "calendar", permission: "finance:read" },
      { key: "bank", label: "Banche", to: "/sales/bank", icon: "bank", permission: "finance:read" },
      { key: "ledger", label: "Prima nota", to: "/sales/ledger", icon: "book", permission: "finance:read" },
      { key: "products", label: "Prodotti", to: "/sales/products", icon: "box" },
      { key: "reports", label: "Report", to: "/sales/reports", icon: "report", permission: "finance:read" },
    ],
    settings: [
      { key: "defaultVatRate", label: "Aliquota IVA predefinita", type: "number", default: 22 },
      { key: "paymentTermsDays", label: "Termini di pagamento (giorni)", type: "number", default: 30 },
      { key: "quoteExpiryDays", label: "Avvisa prima della scadenza dei preventivi inviati (giorni)", type: "number", default: 5 },
      { key: "coaCustomers", label: "Conto clienti (registrazioni per il commercialista)", type: "string", default: "Crediti verso clienti" },
      { key: "coaSuppliers", label: "Conto fornitori", type: "string", default: "Debiti verso fornitori" },
      { key: "coaRevenue", label: "Conto ricavi", type: "string", default: "Ricavi per prestazioni di servizi" },
      { key: "coaVatSales", label: "Conto IVA a debito", type: "string", default: "IVA a debito" },
      { key: "coaVatPurchases", label: "Conto IVA a credito", type: "string", default: "IVA a credito" },
      { key: "coaCosts", label: "Conto costi (fatture passive)", type: "string", default: "Costi per servizi" },
    ],
    events: ["sales.quote.sent", "sales.quote.accepted", "sales.invoice.issued", "sales.invoice.paid", "sales.invoice.overdue"],
    notifications: [
      { type: "quote_viewed", label: "Il cliente apre un preventivo", email: false },
      { type: "quote_accepted", label: "Il cliente accetta o rifiuta un preventivo", email: true },
      { type: "invoice_overdue", label: "Fatture scadute da sollecitare", email: true },
      { type: "sdi_outcome", label: "Esito dell'invio allo SDI", email: true },
      { type: "quote_expiring", label: "Preventivi inviati in scadenza senza risposta", email: true },
    ],
    aiTools: [
      { name: "sales_overdue_invoices", description: "Fatture scadute e da incassare", permission: "invoices:read", mutates: false },
      { name: "sales_cashflow", description: "Entrate e uscite previste per i prossimi mesi", permission: "finance:read", mutates: false },
      { name: "sales_draft_quote", description: "Prepara la bozza di un preventivo", permission: "quotes:write", mutates: true },
      { name: "sales_forecast", description: "Previsione del fatturato a fine anno (fatturato, commesse, contratti in approvazione, pipeline)", permission: "finance:read", mutates: false },
    ],
    jobs: [{ key: "sales.overdue", description: "Marca le fatture scadute e notifica", cron: "0 8 * * *" }],
    extensions: {
      entityTabs: [{ entity: "company", key: "invoices", label: "Fatture", icon: "receipt" }],
      actions: [
        { entity: "deal", key: "create-quote", label: "Prepara preventivo", icon: "file-text", permission: "quotes:write" },
        { entity: "project", key: "invoice-billable", label: "Fattura le ore", icon: "receipt", permission: "invoices:write" },
      ],
      dashboardWidgets: [{ key: "sales.cash", label: "Incassi del mese", size: "m" }],
      automationTriggers: [{ key: "sales.invoice.paid", label: "Fattura pagata" }],
      documentTypes: [
        { key: "quote", label: "Preventivo", prefix: "PRE" },
        { key: "invoice", label: "Fattura", prefix: "FT" },
      ],
    },
  },
  marketing: {
    key: "marketing",
    entryPermission: "marketing:read",
    name: "Marketing",
    color: "#7C3AED",
    tint: "#F1EAFD",
    ink: "#5B21B6",
    icon: "megaphone",
    description: "Campagne email, template, segmenti, form e automazioni.",
    dependsOn: ["crm"],
    permissions: ["marketing:read", "marketing:write"],
    navigation: [
      { key: "campaigns", label: "Campagne", to: "/marketing", icon: "megaphone" },
      { key: "templates", label: "Template", to: "/marketing/templates", icon: "template" },
      { key: "segments", label: "Segmenti", to: "/marketing/segments", icon: "segment" },
      { key: "lists", label: "Liste", to: "/marketing/lists", icon: "list" },
      { key: "forms", label: "Form", to: "/marketing/forms", icon: "form" },
      { key: "automations", label: "Automazioni", to: "/marketing/automations", icon: "flow" },
    ],
    settings: [],
    events: ["marketing.campaign.sent", "marketing.form.submitted", "marketing.email.opened"],
    notifications: [{ type: "form_submitted", label: "Nuovo contatto da un form", email: false }],
    aiTools: [
      { name: "marketing_campaign_stats", description: "Aperture e clic delle ultime campagne", permission: "marketing:read", mutates: false },
      { name: "marketing_draft_email", description: "Scrive la bozza di una email di campagna", permission: "marketing:write", mutates: true },
    ],
    jobs: [{ key: "marketing.automations", description: "Avanza le automazioni in attesa", cron: "*/5 * * * *" }],
    extensions: {
      entityTabs: [{ entity: "contact", key: "emails", label: "Email", icon: "mail" }],
      dashboardWidgets: [{ key: "marketing.last-campaign", label: "Ultima campagna", size: "s" }],
      automationTriggers: [{ key: "marketing.form.submitted", label: "Form compilato" }],
      automationActions: [{ key: "marketing.send_email", label: "Invia email" }],
    },
  },
  contracts: {
    key: "contracts",
    entryPermission: "contracts:read",
    name: "Commesse",
    color: "#0891B2",
    tint: "#E3F5FA",
    ink: "#0E6C85",
    icon: "briefcase",
    description: "Contratti e commesse: tariffe per livello o a giornata, piano di fatturazione, trasferte con tetto, marginalità e provvigioni.",
    dependsOn: ["projects", "sales"],
    permissions: ["contracts:read", "contracts:write", "contracts:economics"],
    navigation: [
      { key: "all", label: "Commesse", to: "/contracts", icon: "briefcase" },
      { key: "billing", label: "Da fatturare", to: "/contracts/billing", icon: "receipt", permission: "contracts:write" },
      { key: "reports", label: "Marginalità e quote", to: "/contracts/reports", icon: "report", permission: "contracts:economics" },
    ],
    settings: [
      { key: "expiringDays", label: "Avvisa prima della scadenza del contratto (giorni)", type: "number", default: 30 },
      { key: "billingDueDays", label: "Avvisa prima delle rate del piano (giorni)", type: "number", default: 7 },
    ],
    events: ["contracts.contract.created", "contracts.contract.activated", "contracts.contract.closed", "contracts.contract.invoiced"],
    notifications: [
      { type: "contract_expiring", label: "Un contratto è in scadenza", email: true },
      { type: "contract_billing_due", label: "Rate del piano di fatturazione da emettere", email: true },
    ],
    aiTools: [
      { name: "contracts_list", description: "Commesse attive con valore, fatturato, residuo e scadenza", permission: "contracts:read", mutates: false },
      { name: "contracts_margin", description: "Ricavi, costi (personale, spese, fornitori), margine e quote di una commessa", permission: "contracts:economics", mutates: false },
      { name: "contracts_to_bill", description: "Cosa c'è da fatturare sulle commesse (ore approvate, rate scadute, spese)", permission: "contracts:read", mutates: false },
    ],
    jobs: [{ key: "contracts.daily", description: "Contratti in scadenza e rate del piano da fatturare", cron: "45 7 * * 1-5" }],
    extensions: {
      entityTabs: [{ entity: "company", key: "contracts", label: "Commesse", icon: "briefcase" }],
      actions: [{ entity: "deal", key: "create-contract", label: "Crea commessa dal deal", icon: "briefcase", permission: "contracts:write" }],
      dashboardWidgets: [{ key: "contracts.margin", label: "Commesse", size: "m" }],
      automationTriggers: [{ key: "contracts.contract.activated", label: "Commessa attivata" }],
      documentTypes: [{ key: "contract", label: "Commessa", prefix: "CO" }],
    },
  },
  expenses: {
    key: "expenses",
    entryPermission: "expenses:read",
    name: "Spese",
    color: "#DB2777",
    tint: "#FCE8F2",
    ink: "#9D174D",
    icon: "card",
    description: "Note spese con giustificativi, rimborsi chilometrici, approvazione, rimborso in prima nota e rifatturazione sulle commesse.",
    dependsOn: [],
    permissions: ["expenses:read", "expenses:write", "expenses:approve", "expenses:read_all"],
    navigation: [
      { key: "mine", label: "Le mie spese", to: "/expenses", icon: "card" },
      { key: "approvals", label: "Da approvare", to: "/expenses/approvals", icon: "check-list", permission: "expenses:approve" },
      { key: "report", label: "Report per persona", to: "/expenses/report", icon: "report", permission: "expenses:read_all" },
    ],
    settings: [
      { key: "kmRateCents", label: "Rimborso chilometrico (centesimi al km)", type: "number", default: 40 },
      { key: "requireReceipt", label: "Giustificativo obbligatorio (escluso il rimborso km)", type: "boolean", default: true },
    ],
    events: ["expenses.expense.submitted", "expenses.expense.approved", "expenses.expense.reimbursed"],
    notifications: [
      { type: "expense_submitted", label: "Note spese da approvare", email: true },
      { type: "expense_reviewed", label: "Esito delle mie note spese", email: true },
    ],
    aiTools: [
      { name: "expenses_summary", description: "Spese per persona, categoria e commessa nel periodo", permission: "expenses:read", mutates: false },
      { name: "expenses_pending", description: "Note spese in attesa di approvazione o di rimborso", permission: "expenses:approve", mutates: false },
    ],
    jobs: [],
    extensions: {
      dashboardWidgets: [{ key: "expenses.pending", label: "Note spese", size: "s" }],
      documentTypes: [],
    },
  },
  ai: {
    key: "ai",
    entryPermission: "ai:use",
    name: "Kipo AI",
    color: "#101214",
    tint: "#F0F0ED",
    ink: "#101214",
    icon: "sparkles",
    description: "Kipo AI: legge e agisce su tutti i moduli attivi tramite i loro tool.",
    dependsOn: [],
    permissions: ["ai:use", "ai:configure"],
    navigation: [{ key: "chat", label: "Kipo AI", to: "/assistant", icon: "sparkles" }],
    settings: [],
    events: [],
    notifications: [],
    aiTools: [
      { name: "core_search", description: "Ricerca globale su tutta la suite", permission: "ai:use", mutates: false },
      { name: "core_today", description: "Agenda di oggi: task, attività, scadenze", permission: "ai:use", mutates: false },
      { name: "email_search", description: "Cerca nella casella email condivisa", permission: "mailbox:read", mutates: false },
      { name: "email_read", description: "Legge un'email della casella", permission: "mailbox:read", mutates: false },
      { name: "email_draft_reply", description: "Prepara una bozza di risposta (non la invia)", permission: "mailbox:draft", mutates: true },
    ],
    jobs: [],
    extensions: { dashboardWidgets: [{ key: "ai.insights", label: "Kipo AI segnala", size: "m" }] },
  },
};

export const MODULE_LIST: ModuleManifest[] = MODULE_KEYS.map((k) => MODULES[k]);

export function getModule(key: string): ModuleManifest | undefined {
  return (MODULES as Record<string, ModuleManifest>)[key];
}

/** Moduli da disattivare a cascata se `key` viene spento. */
export function dependentsOf(key: ModuleKey): ModuleKey[] {
  return MODULE_KEYS.filter((k) => MODULES[k].dependsOn.includes(key));
}

/** Verifica che le dipendenze siano soddisfatte da un insieme di moduli attivi. */
export function missingDependencies(key: ModuleKey, active: readonly string[]): ModuleKey[] {
  return MODULES[key].dependsOn.filter((d) => !active.includes(d));
}

/** Palette di brand (proposta grafica C, "Blocchi"). */
export const BRAND = {
  ink: "#101214",
  ground: "#F7F7F5",
  surface: "#FFFFFF",
  line: "#E6E6E2",
  lineSoft: "#F0F0ED",
  muted: "#6B6F76",
  subtle: "#8A8D93",
  text2: "#3C4047",
  warn: "#B4541E",
  warnTint: "#FFF6F0",
} as const;

/** Catalogo delle notifiche configurabili: per modulo, con l'invio email predefinito. */
export function notificationCatalog() {
  return (Object.keys(MODULES) as ModuleKey[]).flatMap((key) => MODULES[key].notifications.map((n) => ({ ...n, module: key })));
}
export function defaultEmailFor(type: string): boolean {
  return notificationCatalog().find((n) => n.type === type)?.email ?? false;
}
